import { useState } from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Chip, IconButton, Tooltip, Box, Typography, CircularProgress } from '@mui/material';
import { Eye, CheckCircle, Hourglass, Circle, AlertCircle } from 'lucide-react';
import { useSubmissions } from '@lingaai/shared-hooks';
import { SubmissionDetailDrawer } from '../SubmissionDetailDrawer';

interface SubmissionHistoryProps {
  companyId: string;
}

type SortField = 'period' | 'status' | 'submitted_at';
type SortOrder = 'asc' | 'desc';

export function SubmissionHistory({ companyId }: SubmissionHistoryProps) {
  const { data: submissions, isLoading } = useSubmissions(companyId);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('submitted_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} strokeWidth={1} color="#2e7d32" />;
      case 'processing':
        return <Hourglass size={16} strokeWidth={1} color="#ed6c02" />;
      case 'needs_review':
        return <AlertCircle size={16} strokeWidth={1} color="#d32f2f" />;
      case 'submitted':
        return <Hourglass size={16} strokeWidth={1} color="#0288d1" />;
      default:
        return <Circle size={16} strokeWidth={1} color="#9e9e9e" />;
    }
  };

  const getStatusLabel = (status: string) => {
    if (status === 'draft') return 'Draft';
    return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'warning';
      case 'submitted':
        return 'info';
      case 'needs_review':
        return 'error';
      default:
        return 'default';
    }
  };

  const sortedSubmissions = submissions
    ? [...submissions].sort((a, b) => {
        let comparison = 0;

        switch (sortField) {
          case 'period':
            comparison = a.period.localeCompare(b.period);
            break;
          case 'status': {
            const statusOrder = {
              draft: 0,
              processing: 1,
              submitted: 2,
              needs_review: 3,
              completed: 4,
            };
            const aOrder = statusOrder[a.status as keyof typeof statusOrder] ?? 0;
            const bOrder = statusOrder[b.status as keyof typeof statusOrder] ?? 0;
            comparison = aOrder - bOrder;
            break;
          }
          case 'submitted_at': {
            const aDate = a.submitted_at ? new Date(a.submitted_at).getTime() : 0;
            const bDate = b.submitted_at ? new Date(b.submitted_at).getTime() : 0;
            comparison = aDate - bDate;
            break;
          }
        }

        return sortOrder === 'asc' ? comparison : -comparison;
      })
    : [];

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!submissions || submissions.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No submissions yet. Upload your first report to get started.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Submission History
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel active={sortField === 'period'} direction={sortField === 'period' ? sortOrder : 'asc'} onClick={() => handleSort('period')}>
                  Period
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField === 'status'} direction={sortField === 'status' ? sortOrder : 'asc'} onClick={() => handleSort('status')}>
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField === 'submitted_at'} direction={sortField === 'submitted_at' ? sortOrder : 'asc'} onClick={() => handleSort('submitted_at')}>
                  Submitted
                </TableSortLabel>
              </TableCell>
              <TableCell>Source</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedSubmissions.map((submission) => (
              <TableRow key={submission.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getStatusIcon(submission.status)}
                    <Typography variant="body2" fontWeight={600}>
                      {submission.period}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={getStatusLabel(submission.status)} color={getStatusColor(submission.status) as any} size="small" />
                </TableCell>
                <TableCell>{formatDate(submission.submitted_at)}</TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {submission.source_type === 'email' ? 'Email' : 'Upload'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => setSelectedSubmissionId(submission.id)}>
                      <Eye size={16} strokeWidth={1} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <SubmissionDetailDrawer submissionId={selectedSubmissionId} open={!!selectedSubmissionId} onClose={() => setSelectedSubmissionId(null)} />
    </Box>
  );
}
